import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * Renders a Mermaid diagram from a code string to inline SVG.
 *
 * Mermaid is initialized once (module-level guard) with startOnLoad disabled so
 * we drive rendering imperatively per-diagram. Each render gets a unique id —
 * `mermaid.render` requires a DOM-safe, unique id or it throws on re-render.
 *
 * Kept dependency-light and self-contained so the System Flows page can render
 * many diagrams without a global mermaid <script> or markdown pipeline.
 */

let initialized = false;
let renderSeq = 0;

async function ensureMermaid() {
  // Lazy import keeps mermaid (a heavy dep) out of the initial admin bundle —
  // it only loads when someone opens the How-It-Works page.
  const mermaid = (await import('mermaid')).default;
  if (!initialized) {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'strict', // diagrams are static, author-controlled content
      theme: 'default',
      flowchart: { htmlLabels: true, curve: 'basis', useMaxWidth: true },
    });
    initialized = true;
  }
  return mermaid;
}

interface MermaidDiagramProps {
  /**
   * The Mermaid source (e.g. a `flowchart TD ...` string).
   *
   * SECURITY: this MUST be static, author-controlled content (e.g. the literals
   * in flows-content.ts). The rendered SVG is injected via innerHTML — never
   * wire user-supplied or fetched data into `code`. (mermaid's strict
   * securityLevel + DOMPurify sanitize, but the invariant is the real guard.)
   */
  code: string;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ code }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const mermaid = await ensureMermaid();
        renderSeq += 1;
        const id = `mermaid-svg-${renderSeq}`;
        const { svg } = await mermaid.render(id, code);
        if (cancelled) return;
        if (containerRef.current) containerRef.current.innerHTML = svg;
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to render diagram');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code]);

  return (
    <Box sx={{ position: 'relative', minHeight: 80, width: '100%' }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && (
        <Typography color="error" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
          Diagram failed to render: {error}
        </Typography>
      )}
      <Box
        ref={containerRef}
        data-testid="mermaid-diagram"
        sx={{
          width: '100%',
          overflowX: 'auto',
          '& svg': { maxWidth: '100%', height: 'auto' },
          display: loading || error ? 'none' : 'block',
        }}
      />
    </Box>
  );
};

export default MermaidDiagram;
